import { LitElement, html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import * as founderBoardApi from '../services/founder-board-api.js';
import * as companyApi from '../services/company-api.js';
import type { FounderTicket, FounderTicketStatus } from '../services/founder-board-api.js';
import { statusLabel } from '../services/founder-board-api.js';
import type { Company } from '../services/company-api.js';
import { navigateTo } from '../services/router.js';
import { wsClient } from '../services/websocket-client.js';
import '../components/company/cp-founder-ticket-card.js';
import '../components/shared/cp-page-subnav.js';

const COMPANY_TABS = [
  { label: 'Firmen', path: '/companies' },
  { label: 'Gruender Board', path: '/founder-board' },
];

interface CompanyTicketSummary {
  company: Company;
  counts: Record<FounderTicketStatus, number>;
  total: number;
  tickets: FounderTicket[];
}

const COLUMNS: FounderTicketStatus[] = ['open', 'waiting', 'approved', 'done'];

@customElement('cp-page-founder-board')
export class CpPageFounderBoard extends LitElement {
  createRenderRoot() { return this; }

  @state() private _tickets: FounderTicket[] = [];
  @state() private _companies: Company[] = [];
  @state() private _loading = false;
  @state() private _error = '';
  @state() private _viewMode: 'overview' | 'all-tickets' = 'overview';

  private _wsHandler: ((e: Event) => void) | null = null;

  override connectedCallback(): void {
    super.connectedCallback();
    void this._load();

    this._wsHandler = (e: Event) => {
      const msg = (e as CustomEvent<{ type: string }>).detail;
      if (msg.type === 'founder-ticket:created' || msg.type === 'founder-ticket:updated') {
        void this._load();
      }
    };
    wsClient.addEventListener('ws-message', this._wsHandler);
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    if (this._wsHandler) {
      wsClient.removeEventListener('ws-message', this._wsHandler);
      this._wsHandler = null;
    }
  }

  private async _load(): Promise<void> {
    this._loading = true;
    this._error = '';
    try {
      const [tickets, companies] = await Promise.all([
        founderBoardApi.listTickets(),
        companyApi.listCompanies('active'),
      ]);
      this._tickets = tickets;
      this._companies = companies;
    } catch (e) {
      this._error = e instanceof Error ? e.message : 'Fehler beim Laden';
    } finally {
      this._loading = false;
    }
  }

  private get _companySummaries(): CompanyTicketSummary[] {
    const byCompany = new Map<number, FounderTicket[]>();
    for (const t of this._tickets) {
      const existing = byCompany.get(t.company_id) ?? [];
      existing.push(t);
      byCompany.set(t.company_id, existing);
    }

    const summaries: CompanyTicketSummary[] = [];
    for (const company of this._companies) {
      const tickets = byCompany.get(company.id) ?? [];
      const counts: Record<FounderTicketStatus, number> = { open: 0, waiting: 0, approved: 0, done: 0 };
      for (const t of tickets) {
        counts[t.status]++;
      }
      if (tickets.length > 0) {
        summaries.push({ company, counts, total: tickets.length, tickets });
      }
    }

    // Sort by open + waiting count DESC
    summaries.sort((a, b) => (b.counts.open + b.counts.waiting) - (a.counts.open + a.counts.waiting));
    return summaries;
  }

  private _ticketsForColumn(status: FounderTicketStatus): FounderTicket[] {
    return this._tickets.filter(t => t.status === status);
  }

  private _handleTicketUpdated(e: CustomEvent<{ ticket: FounderTicket }>): void {
    const updated = e.detail.ticket;
    this._tickets = this._tickets.map(t => (t.id === updated.id ? updated : t));
  }

  private _renderOverview() {
    const summaries = this._companySummaries;
    const totalOpen = this._tickets.filter(t => t.status === 'open').length;
    const totalWaiting = this._tickets.filter(t => t.status === 'waiting').length;

    return html`
      <!-- Summary Stats -->
      <div class="cp-page-founder-board__stats">
        <div class="cp-page-founder-board__stat">
          <span class="cp-page-founder-board__stat-value cp-page-founder-board__stat-value--warning">${totalOpen}</span>
          <span class="cp-page-founder-board__stat-label">Offen</span>
        </div>
        <div class="cp-page-founder-board__stat">
          <span class="cp-page-founder-board__stat-value cp-page-founder-board__stat-value--amber">${totalWaiting}</span>
          <span class="cp-page-founder-board__stat-label">Wartend</span>
        </div>
        <div class="cp-page-founder-board__stat">
          <span class="cp-page-founder-board__stat-value">${this._tickets.length}</span>
          <span class="cp-page-founder-board__stat-label">Gesamt</span>
        </div>
      </div>

      ${summaries.length === 0 ? html`
        <div class="cp-page-founder-board__empty">
          <p>Keine offenen Tickets</p>
          <p class="cp-page-founder-board__empty-sub">Tickets erscheinen hier, wenn Agenten Rueckfragen, Genehmigungen oder Tool-Anfragen erstellen.</p>
        </div>
      ` : nothing}

      <!-- Company Cards with ticket counts -->
      <div class="cp-page-founder-board__company-grid">
        ${summaries.map(s => html`
          <button
            class="cp-page-founder-board__company-card"
            @click=${() => navigateTo(`/companies/${s.company.id}`)}
          >
            <div class="cp-page-founder-board__company-header">
              <div
                class="cp-page-founder-board__company-avatar"
                style="background-color: ${s.company.color ?? '#6B7280'}"
              >
                ${s.company.icon ?? s.company.name.charAt(0).toUpperCase()}
              </div>
              <div class="cp-page-founder-board__company-info">
                <p class="cp-page-founder-board__company-name">${s.company.name}</p>
                <p class="cp-page-founder-board__company-type">${s.company.type || 'Unternehmen'}</p>
              </div>
              ${(s.counts.open + s.counts.waiting) > 0 ? html`
                <span class="cp-page-founder-board__company-badge">
                  ${s.counts.open + s.counts.waiting}
                </span>
              ` : nothing}
            </div>
            <div class="cp-page-founder-board__company-counts">
              ${COLUMNS.map(status => html`
                <span class="cp-page-founder-board__count-chip ${s.counts[status] > 0 ? `cp-page-founder-board__count-chip--${status}` : ''}">
                  ${s.counts[status]} ${statusLabel(status)}
                </span>
              `)}
            </div>
          </button>
        `)}
      </div>
    `;
  }

  private _renderAllTickets() {
    return html`
      <div class="cp-page-founder-board__columns">
        ${COLUMNS.map(status => this._renderColumn(status))}
      </div>
    `;
  }

  override render() {
    return html`
      <div class="cp-page">
        <cp-page-subnav .tabs=${COMPANY_TABS} activePath="/founder-board"></cp-page-subnav>
        <div class="cp-page-founder-board__header">
          <div class="cp-page-founder-board__header-row">
            <div>
              <h1 class="cp-page-founder-board__title">Gruender Control Board</h1>
              <p class="cp-page-founder-board__subtitle">Tickets deiner Agenten – Approvals, Rueckfragen, Tool-Anfragen</p>
            </div>
            <div class="cp-page-founder-board__header-actions">
              ${this._loading ? html`<span class="cp-page-founder-board__loading">Lade...</span>` : nothing}
              <div class="cp-page-founder-board__view-toggle">
                <button
                  class="cp-page-founder-board__view-btn ${this._viewMode === 'overview' ? 'cp-page-founder-board__view-btn--active' : ''}"
                  @click=${() => { this._viewMode = 'overview'; }}
                >
                  Uebersicht
                </button>
                <button
                  class="cp-page-founder-board__view-btn ${this._viewMode === 'all-tickets' ? 'cp-page-founder-board__view-btn--active' : ''}"
                  @click=${() => { this._viewMode = 'all-tickets'; }}
                >
                  Alle Tickets
                </button>
              </div>
            </div>
          </div>
        </div>

        ${this._error ? html`
          <div class="cp-page-founder-board__error">${this._error}</div>
        ` : nothing}

        ${this._viewMode === 'overview' ? this._renderOverview() : this._renderAllTickets()}
      </div>
    `;
  }

  private _renderColumn(status: FounderTicketStatus) {
    const tickets = this._ticketsForColumn(status);
    const isWaiting = status === 'waiting';

    return html`
      <div class="cp-page-founder-board__column">
        <div class="cp-page-founder-board__column-header">
          <span class="cp-page-founder-board__column-label">${statusLabel(status)}</span>
          ${tickets.length > 0 ? html`
            <span class="cp-page-founder-board__column-count ${isWaiting ? 'cp-page-founder-board__column-count--waiting' : ''}">${tickets.length}</span>
          ` : nothing}
        </div>

        <div class="cp-page-founder-board__column-body">
          ${tickets.length === 0 ? html`
            <p class="cp-page-founder-board__column-empty">Keine Tickets</p>
          ` : nothing}
          ${tickets.map(ticket => html`
            <cp-founder-ticket-card
              .ticket=${ticket}
              @ticket-updated=${(e: CustomEvent<{ ticket: FounderTicket }>) => this._handleTicketUpdated(e)}
            ></cp-founder-ticket-card>
          `)}
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'cp-page-founder-board': CpPageFounderBoard;
  }
}
